import { LanguageSelector, translate, useLanguage } from "@cqt/platform";
/** The signed-in person's ordinary profile fields. Access and email stay elsewhere. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AutosaveStatus, ErrorBox, Field, Loading, useApi, useAuth, useAutosave } from "@cqt/platform";
import { Avatar, displayName } from "./Avatar";

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  username: string | null;
  title: string | null;
  phone: string | null;
  avatar_url: string | null;
  profile_complete: boolean;
}

type Editable = "full_name" | "username" | "title" | "phone";
const FIELDS: { key: Editable; label: string; hint?: string; placeholder?: string }[] = [
  { key: "full_name", label: "Full name", hint: "How colleagues see you" },
  { key: "username", label: "Username", hint: "Lowercase letters, numbers, dot, dash, underscore. Used for @mentions.", placeholder: "jake" },
  { key: "title", label: "Job title", placeholder: "Operations Manager" },
  { key: "phone", label: "Phone", hint: "Whatever format your team dials" },
];
const valuesOf = (profile: Profile | undefined): Record<Editable, string | null> => ({
  full_name: profile?.full_name ?? null,
  username: profile?.username ?? null,
  title: profile?.title ?? null,
  phone: profile?.phone ?? null,
});

export function ProfileTab() {
  useLanguage();
  const api = useApi();
  const { me, refreshMe } = useAuth();
  const qc = useQueryClient();
  const current = useQuery({ queryKey: ["me", "profile", me.user.id], queryFn: () => api.get<Profile>("/me/profile") });
  const autosave = useAutosave({
    storageKey: `profile:${me.user.id}:${me.company?.id ?? "platform"}:${me.user.id}`,
    values: valuesOf(current.data),
    enabled: current.isSuccess,
    save: async (changes, expected) => {
      const saved = await api.patch<Profile>("/me/profile", { ...changes, expected_values: expected });
      // The controller retains newer keystrokes and acknowledged values. Update
      // the query from this response; older refetches cannot erase its draft.
      qc.setQueryData<Profile>(["me", "profile", me.user.id], saved);
      void refreshMe();
    },
  });

  if (current.isPending) return <Loading label={translate("Loading your profile")} />;
  if (current.error) return <ErrorBox error={current.error} />;
  if (!current.data) return null;
  const saved = current.data;
  const handle = String(autosave.values.username ?? "");
  const handleOk = handle === "" || /^[a-z0-9][a-z0-9._-]{1,39}$/.test(handle);

  return <div className="card">
    <div className="card__head">
      <div className="row" style={{ gap: 14, alignItems: "center" }}>
        <Avatar person={saved} size={52} />
        <div>
          <h3 style={{ margin: 0 }}>{displayName(saved)}</h3>
          <p className="meta" style={{ margin: 0 }}>{saved.email}{me.is_superadmin ? translate(" · platform administrator") : ""}</p>
        </div>
      </div>
    </div>
    <div className="card__body">
      {!saved.profile_complete ? <p className="notice">{translate("Welcome. Filling this in is how colleagues recognise you in the CRM and anywhere work gets assigned.")}</p> : null}
      <LanguageSelector />
      <div className="profile-form">
        {FIELDS.map(({ key, label, hint, placeholder }) =>
          <Field key={key} label={translate(label)} htmlFor={`profile-${key}`} hint={hint ? translate(hint) : hint}>
            <input id={`profile-${key}`} className="input" value={String(autosave.values[key] ?? "")}
              placeholder={placeholder ? translate(placeholder) : placeholder} disabled={!autosave.ready}
              onChange={(event) => autosave.setValue(key, (key === "username" ? event.target.value.toLowerCase() : event.target.value) || null)} />
          </Field>
        )}
        {!handleOk ? <p className="meta">{translate("A username needs at least two characters and can use only lowercase letters, numbers, dot, dash and underscore.")}</p> : null}
        <p className="hint">{translate("Profile fields save automatically after you pause typing. Wait for Saved before closing this browser. Interrupted drafts resume in this tab.")} <a href="/profile-saving-guide.html" target="_blank" rel="noreferrer">{translate("Profile saving guide")}</a></p>
        <AutosaveStatus state={autosave} onDiscard={async () => {
          const fresh = await current.refetch();
          if (fresh.data) autosave.acceptAuthoritativeValues(valuesOf(fresh.data));
        }} />
      </div>
    </div>
  </div>;
}
